/**
 * Single source of truth for static asset URLs.
 *
 * Every image the app renders is served from the DigitalOcean Spaces CDN
 * (`infynd-app.lon1.cdn.digitaloceanspaces.com`) rather than from
 * `packages/client/public`, so the standalone build ships no binaries and a
 * logo swap is an upload, not a redeploy.
 *
 * Use `cdn()` for a one-off path and `CDN_ASSETS` for anything referenced from
 * more than one place — the registry is what keeps the bucket's layout in one
 * file. That layout is NOT a mirror of the old `public/Images` tree: the logo
 * and favicon sit flat under `/images/`, while the partner logos sit at the
 * bucket root under `/partners/`. Nobody should have to remember that, which
 * is exactly why callers import a named constant instead of writing a path.
 */

/**
 * Overridable per environment via `NEXT_PUBLIC_CDN_BASE_URL` (see `.env.dev` /
 * `.env.production`) so a staging bucket can be pointed at without a code
 * change. The literal is the fallback rather than the only value: a missing env
 * var in some future build pipeline should degrade to the live CDN, not to
 * broken <img> tags. Trailing slashes are trimmed so joining is unambiguous.
 */
export const CDN_BASE_URL = (
  process.env.NEXT_PUBLIC_CDN_BASE_URL || "https://infynd-app.lon1.cdn.digitaloceanspaces.com"
).replace(/\/+$/, "");

/**
 * Builds an absolute CDN URL from a bucket-relative path. Leading slash
 * optional — `cdn("images/x.png")` and `cdn("/images/x.png")` are the same.
 *
 * Already-absolute URLs pass through untouched, so this is safe to wrap around
 * a value that may already be a full URL (an API-supplied avatar, say).
 */
export function cdn(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${CDN_BASE_URL}/${path.replace(/^\/+/, "")}`;
}

/**
 * Company logos live at the bucket root under `/company_logos/{id}.png` (not
 * `/images/`, and not part of `CDN_ASSETS` since the path is per-company
 * rather than a fixed name).
 *
 * Whatever id a caller has (a real upstream company id, a dropdown-service
 * value) is used as-is - there's no lookup to confirm the file exists. Most
 * companies don't have an uploaded logo, so a 404 here is the common case,
 * not an error; callers render this through `<AvatarImage>` (shadcn's Avatar
 * swaps to `<AvatarFallback>` on a failed load), so it degrades to a generic
 * icon instead of a broken image.
 */
export function getCompanyLogoUrl(companyId: string | number): string {
  return cdn(`/company_logos/${companyId}.png`);
}

/**
 * Named assets. Add to this registry instead of sprinkling `cdn("...")` string
 * literals through components — a renamed or re-uploaded file then changes in
 * one place, and a typo is a compile error rather than a silent 403.
 */
export const CDN_ASSETS = {
  /** Full wordmark. Intrinsic size 205x70-ish; callers set their own box. */
  logo: cdn("/images/infynd_logo-BjUV5geE.png"),
  /** Square mark, used for the favicon and the collapsed sidebar. */
  favicon: cdn("/images/InFyndFavIcon.svg"),
  /** CRM provider logos, under `/crm/`. Rendered in the CRM settings rows and dialogs. */
  crm: {
    hubspot: cdn("/crm/hubspot.png"),
    salesforce: cdn("/crm/salesforce.png"),
    dynamics365: cdn("/crm/dynamics-365.png"),
  },
  /** Customer logos for the auth-page carousel. Keyed, not an array, so the
   *  order and the per-logo display tweaks stay with the UI that renders them. */
  partners: {
    britannia: cdn("/partners/britannia-carousel.png"),
    cvlibrary: cdn("/partners/cvlibrary-carousel.png"),
    deliveroo: cdn("/partners/deliveroo-carousel.png"),
    experian: cdn("/partners/experian-carousel.png"),
    silveyfleet: cdn("/partners/silveyfleet-carousel.png"),
    sony: cdn("/partners/sony-carousel.png"),
  },
} as const;
